import { EHttpMethods } from "../../../../enums";
import { AUTH_BASE_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { AuthConfig } from "../../../context/config/authConfig";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import {
  ACTION_DEACTIVE_AND_ACTIVE,
  LIST_PROFILES_URL,
  PROFILE_URL,
  REGISTRATION_PATH,
  USERS_URL,
} from "./constants";
import {
  IClientAdminBody,
  IClientAdminBodyResponse,
  IRegisterByPhoneRequest,
  IRegisterByPhoneResponse,
  IPinCodeRequestBody,
  ISetPinCodeResponse,
  IUpdateNationalIdRequestBody,
  IUpdateNationalIdResponse,
  IVerifyPincodeResponse,
  IUserAddressResponse,
  IUserInfoResponse,
  IUserAllAddressResponse,
  IUpdatePinCodeRequestBody,
  IUserAddressBody,
  IUserAllInfoResponse,
  IRequestResponse,
  IOrgOwnerBodyResponse,
  IBankAddressStatusResponse,
  IPhoneRuleConfigsResponse,
  IInviteResponse,
  IClientAdminSignUpResponse,
} from "./models";

// eslint-disable-next-line no-unused-vars
const defaultProvide = async () => await getToken();
const appProvide = async () => AuthConfig.getAppToken();

export const registerByPhone = (
  body: IRegisterByPhoneRequest
): Promise<IRegisterByPhoneResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}${REGISTRATION_PATH}`,
    "POST",
    appProvide
  );
  return fetchRequest(descriptor, body);
};

export const postUserPinCode = (
  userId: string,
  body: IPinCodeRequestBody
): Promise<ISetPinCodeResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/api/users/${userId}/pin-code`,
    "POST",
    appProvide
  );
  return fetchRequest(descriptor, body);
};

export const getClientAdminOrgAdmin = (userId: string): Promise<any> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/api/users/orgAdmin/client/${userId}`,
    "GET",
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const updateNationalId = (
  userId: string,
  body: IUpdateNationalIdRequestBody
): Promise<IUpdateNationalIdResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/api/users/${userId}/national-id`,
    "PATCH",
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};
export const getBasicUserDetailsById = (
  id: string | number
): Promise<IUserInfoResponse> => {
  const url = `${AUTH_BASE_URL}${PROFILE_URL}/${id}/basic-info`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const getBankAddressStatus = (
  ids: (string | number)[]
): Promise<IBankAddressStatusResponse> => {
  const url = `${AUTH_BASE_URL}/api/users/bank-address-status?ids=${ids.join(
    ","
  )}`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const getUserAddress = (id: string): Promise<IUserAddressResponse> => {
  const url = `${AUTH_BASE_URL}/api/addresses/${id}`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const verifyPinCode = (
  userId: string,
  body: IPinCodeRequestBody
): Promise<IVerifyPincodeResponse> => {
  const url = `${AUTH_BASE_URL}/api/users/${userId}/pin-code/verify`;
  const descriptor = new ApiServiceManager(url, "POST", defaultProvide);
  return fetchRequest(descriptor, body);
};

export const updatePinCode = (
  userId: string,
  body: IUpdatePinCodeRequestBody
): Promise<IVerifyPincodeResponse> => {
  const url = `${AUTH_BASE_URL}/api/users/${userId}/pin-code`;
  const descriptor = new ApiServiceManager(url, "PATCH", defaultProvide);
  return fetchRequest(descriptor, body);
};

export const getUserAllAddress = (
  id: number
): Promise<IUserAllAddressResponse> => {
  const url = `${AUTH_BASE_URL}${PROFILE_URL}/${id}/addresses`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const addUserAddress = (id: number, body: IUserAddressBody) => {
  const url = `${AUTH_BASE_URL}${PROFILE_URL}/${id}/addresses`;
  const descriptor = new ApiServiceManager(url, "POST", defaultProvide);
  return fetchRequest(descriptor, body);
};

export const editUserAddress = (id: number, body: IUserAddressBody) => {
  const url = `${AUTH_BASE_URL}/api/addresses/${id}`;
  const descriptor = new ApiServiceManager(url, "PUT", defaultProvide);
  return fetchRequest(descriptor, body);
};

export const getAllUsers = (
  page = 0,
  size = 10,
  searchValue = "",
  signal?: AbortSignal
): Promise<any> => {
  const encoded = searchValue.replace("+", "%2B");
  const url = `${AUTH_BASE_URL}${LIST_PROFILES_URL}?searchKeyWord=${encoded}&page=${page}&size=${size}&sort=createdAt,DESC`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor, undefined, undefined, signal);
};

export const actionForActiveAndDeactive = (id, body) => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}${ACTION_DEACTIVE_AND_ACTIVE(id)}`,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getUserDetailsById = (
  id: string | number
): Promise<IUserAllInfoResponse> => {
  const url = `${AUTH_BASE_URL}${PROFILE_URL}/${id}`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const makeVerifiedUserRequest = (
  userId: string,
  actionType: string
): Promise<IRequestResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}${USERS_URL}/certificate/user/${userId}`,
    EHttpMethods.POST,
    defaultProvide
  );

  // TODO: It's now adjusted to auto approve, we can use the below functions later and send actual documanets and description later
  return fetchRequest(descriptor, {
    type: actionType,
    documnets: ["", ""],
    description: "",
  });
};

export const approveBrokerRequest = (
  userId: string
): Promise<IRequestResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}${USERS_URL}/certificate/approve/${userId}`,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, {});
};

export const rejectBrokerRequest = (
  userId: string,
  reason: string
): Promise<IRequestResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}${USERS_URL}/certificate/reject/${userId}`,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { rejectionReason: reason });
};

export const unlockUserRequest = (
  userId: string
): Promise<IRequestResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}${USERS_URL}/${userId}/unlock`,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, {});
};

export const getClientAdmins = (
  orgCode?: string,
  page = 0,
  size = 10
): Promise<IClientAdminBodyResponse> => {
  const url = `${AUTH_BASE_URL}/v2/api/users/client-admins${
    orgCode ? `?orgCode=${orgCode}` : ""
  }&page=${page}&size=${size}`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const getOrgOwnerByOrgCode = (
  orgCode: string
): Promise<IOrgOwnerBodyResponse> => {
  const url = `${AUTH_BASE_URL}/v2/api/users/organization-admin/${orgCode}`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  return fetchRequest(descriptor);
};

export const createClientAdmin = (
  body: Partial<IClientAdminBody>
): Promise<IClientAdminSignUpResponse> => {
  const url = `${AUTH_BASE_URL}/v2/api/users/client-admin-sign-up`;
  const descriptor = new ApiServiceManager(url, "POST", defaultProvide);
  return fetchRequest(descriptor, body);
};

// TODO: not ready
export const editClientAdmin = (
  id: string,
  body: Partial<IClientAdminBody>
) => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/api/profiles/${id}/profile`,
    "PATCH",
    appProvide
  );
  return fetchRequest(descriptor, body);
};

export const editClientAdminPermissions = (
  id: number,
  body: Partial<IClientAdminBody>
) => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/v2/api/users/${id}/client-admin-authorities`,
    "PUT",
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/** Enabled/disabled phone rules per country, the same list the Waffy ID registration form uses. */
export const getPhoneRuleConfigs = (): Promise<IPhoneRuleConfigsResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/api/phone-rule-configs`,
    "GET",
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Sends the invitation email again to a user who has not chosen a password yet. */
export const resendClientAdminInvite = (
  id: number | string
): Promise<IInviteResponse> => {
  const descriptor = new ApiServiceManager(
    `${AUTH_BASE_URL}/v2/api/users/${id}/resend-invite`,
    "POST",
    defaultProvide
  );
  return fetchRequest(descriptor);
};
